import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Send } from 'lucide-react'
import { listTransactionTypes } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { money } from '../sales/salesShared'
import { TransactionTypesGuide } from '../master/MasterGuides'
import AccountingTabs from './AccountingTabs'
import { shareText } from './transactionTypes'

const sideText = (t, side) => t.lines.filter((l) => l.side === side)
  .map((l) => `${l.account_code} ${l.account_name}${l.share === 'rest' ? '' : ` (${shareText(l, money)})`}`).join(', ')

// Transaction types: your own kinds of transaction (gift cards sold, tips
// paid out...), each a set of debits and credits recorded with one amount.
export default function TransactionTypes() {
  const [types, setTypes] = useState(null)
  const [error, setError] = useState('')
  const [show, setShow] = useState('active')
  useEffect(() => { listTransactionTypes().then(setTypes).catch((e) => { setTypes([]); setError(e.message || 'Couldn’t load the transaction types.') }) }, [])
  const counts = useMemo(() => ({ active: (types || []).filter((t) => t.is_active).length, archived: (types || []).filter((t) => !t.is_active).length }), [types])
  const shown = (types || []).filter((t) => (show === 'active' ? t.is_active : !t.is_active))

  return (
    <div className="ci-page">
      <AccountingTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Transaction types <HelpTip text="Your own kinds of transaction that the app doesn’t write by itself, such as gift cards sold or tips paid out. Set up once which accounts are debited and credited (and how an amount is split); then record one with just an amount, a date and a note." /></h1>
            <div className="ci-title-meta">Your own kinds of transaction, recorded with an amount and a date.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link data-write className="ci-btn ci-btn-ghost" to="/accounting/transaction-types/new"><Plus size={15} />New type</Link>
          {counts.active > 0 && <Link data-write className="ci-btn ci-btn-primary" to="/accounting/transaction-types/record"><Send size={15} />Record a transaction</Link>}
        </div>
      </div>

      <TransactionTypesGuide />
      {error && <ErrorBanner message={error} />}
      <FilterChips options={[{ id: 'active', label: 'In use', count: counts.active }, { id: 'archived', label: 'Archived', count: counts.archived }]} value={show} onChange={setShow} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead><tr><th>Type</th><th className="hide-sm">Debits</th><th className="hide-sm">Credits</th><th className="num hide-sm">Recorded</th><th /></tr></thead>
            <tbody>
              {!types ? (
                <tr><td colSpan="5"><Spinner label="Loading transaction types…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="5"><div className="ci-empty">{show === 'active' ? <>No transaction types yet. <Link to="/accounting/transaction-types/new">Set up your first one</Link>, for example gift cards sold.</> : 'Nothing archived.'}</div></td></tr>
              ) : shown.map((t) => (
                <tr key={t.type_id}>
                  <td>
                    <Link className="ci-strong mf-row-link" to={`/accounting/transaction-types/${t.type_id}`}>{t.name}</Link>
                    {!t.is_active && <> <StatusBadge label="Archived" tone="muted" /></>}
                    <span className="cell-sub">{t.description || `${t.lines.length} lines`}</span>
                  </td>
                  <td className="hide-sm ci-muted-cell">{sideText(t, 'debit')}</td>
                  <td className="hide-sm ci-muted-cell">{sideText(t, 'credit')}</td>
                  <td className="num hide-sm">{t.times_used}</td>
                  <td className="num">{t.is_active && <Link to={`/accounting/transaction-types/record?type=${t.type_id}`}>Record</Link>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
